import React, { useReducer, useEffect } from 'react';
import { css } from '@compiled/react';

import Spinner from '@atlaskit/spinner/spinner';

import { fg } from '@confluence/feature-gating';

import { useStaticMacroRenderAnalytics } from '../hooks/useStaticMacroRenderAnalytics.ts';

import { useStaticMacroStoreContext } from './StaticMacroStoreContext.tsx';

const spinnerWrapperStyles = css({ overflow: 'hidden' });

export const REFETCH_DELAY_MS = 7000;
export const POST_EDIT_RETRY_DELAY_MS = 5000;

export const INSERTION_REFETCH_START_DELAY_MS = 10000;
export const INSERTION_REFETCH_GIVE_UP_DELAY_MS = 60_000;

type ForgeMacroProps = {
	localId?: string;
	extensionKey?: string;
	guestParams?: unknown;
	renderIframe: () => React.ReactElement;
	isEditing?: boolean;
	isLivePage?: boolean;
	// True for an untouched Connect macro (extensionType `com.atlassian.confluence.macro.core`).
	isConnectShapedNode?: boolean;
	nestedMacroLocalIds?: string[];
};

function getNestedMacroLocalIds(node: any): string[] {
	const localIds = new Set<string>();
	const visit = (nodes: unknown) => {
		const childNodes = Array.isArray(nodes)
			? nodes
			: typeof nodes === 'object' && nodes !== null && Array.isArray((nodes as any).content)
				? (nodes as any).content
				: [];
		childNodes.forEach((child: any) => {
			const type = typeof child.type === 'string' ? child.type : child.type?.name;
			if (['extension', 'bodiedExtension', 'inlineExtension'].includes(type)) {
				const localId =
					child.localId ??
					child.attrs?.localId ??
					child.parameters?.localId ??
					child.attrs?.parameters?.localId;
				if (localId) {
					localIds.add(localId);
				}
			}
			visit(child.content);
		});
	};
	visit(node?.content);
	return Array.from(localIds);
}

export function createForgeMacroExtensionHandler(
	isEditing?: boolean,
	isLivePage?: boolean,
):
	| ((node: any, renderFn: (param: { references: any[] }) => JSX.Element) => JSX.Element)
	| undefined {
	if (!fg('conf_frontend_forge_static_macro_mvp')) {
		return undefined;
	}

	return (node: any, renderFn: (param: { references: any[] }) => JSX.Element): JSX.Element => {
		// Untouched Connect macros keep extensionType `com.atlassian.confluence.macro.core` and are
		// keyed in `confluence_staticMacros` by `parameters.macroMetadata.macroId`, not `localId`.
		const isConnectShapedNode = node?.extensionType === 'com.atlassian.confluence.macro.core';
		const localId = isConnectShapedNode
			? node?.parameters?.macroMetadata?.macroId?.value
			: (node?.localId ?? node?.parameters?.localId);
		return (
			<ForgeMacro
				localId={localId}
				extensionKey={node?.extensionKey?.split('/').at(-1)}
				guestParams={node?.parameters?.guestParams || node?.parameters?.config}
				renderIframe={() => renderFn({ references: [] })}
				isEditing={isEditing}
				isLivePage={isLivePage}
				isConnectShapedNode={isConnectShapedNode}
				nestedMacroLocalIds={getNestedMacroLocalIds(node)}
			/>
		);
	};
}

export function ForgeMacro({
	localId,
	extensionKey,
	guestParams,
	renderIframe,
	isEditing,
	isLivePage,
	isConnectShapedNode,
	nestedMacroLocalIds = [],
}: ForgeMacroProps): React.ReactElement {
	const ctx = useStaticMacroStoreContext();
	const store = ctx?.storeRef.current;
	const [, forceUpdate] = useReducer((n: number) => n + 1, 0);
	const [isRefetching, setIsRefetching] = React.useState(false);
	const readyEventEmittedRef = React.useRef(false);
	const postEditTimeoutsRef = React.useRef<ReturnType<typeof setTimeout>[]>([]);
	const cancelPostEditRefetch = React.useCallback(() => {
		postEditTimeoutsRef.current.forEach(clearTimeout);
		postEditTimeoutsRef.current = [];
	}, []);

	// Check if this extension supports staticView, based on preloaded extension data.
	// extensionsLoaded is true when __SSR_FORGE_EXTENSIONS__ has been populated.
	// isStatic may be false either because (a) extensions not loaded yet, or (b) not a static macro.
	// We only use isStatic as a fallback signal when we know extensions ARE loaded.
	//
	// Connect-shaped nodes aren't in that preloaded list, so treat them as "maybe static" and rely
	// on store.fullyLoaded below instead.
	const isStatic = isConnectShapedNode
		? true
		: ctx && extensionKey
			? ctx.isStaticExtension(extensionKey)
			: false;
	const extensionsLoaded = !!window.__SSR_FORGE_EXTENSIONS__?.['xen:macro']?.extensions?.length;

	useEffect(() => {
		if (!ctx || !localId) return;
		const handleUpdate = ({ type }: { type: 'static-macro-result' | 'fully-loaded' }) => {
			// Bulk-query completion changes the fallback decision, but only an update for this macro
			// completes a post-edit refetch. In particular, do not cancel a pending refetch when the
			// initial bulk query happens to complete after a parameter change.
			if (type === 'static-macro-result') {
				cancelPostEditRefetch();
				setIsRefetching(false);
			}
			forceUpdate();
		};
		const unsubscribe = ctx.subscribe(localId, handleUpdate);
		// Data may have already arrived before we subscribed — check immediately.
		if (store?.html.has(localId) || store?.errors.has(localId)) {
			handleUpdate({ type: 'static-macro-result' });
		} else if (store?.fullyLoaded) {
			handleUpdate({ type: 'fully-loaded' });
		}
		return unsubscribe;
	}, [ctx, localId, forceUpdate, store, cancelPostEditRefetch]);

	const startPostEditRefetch = React.useCallback(() => {
		if (!ctx || !localId) return undefined;

		// Hide the now-stale HTML immediately. The delay applies only to the first network request,
		// giving the backend time to render the changed macro state.
		setIsRefetching(true);
		cancelPostEditRefetch();

		const schedulePostEditTimer = (callback: () => void, delay: number) => {
			const timeoutId = setTimeout(callback, delay);
			postEditTimeoutsRef.current.push(timeoutId);
		};

		// A successful refetch (HTML or an error) notifies the store subscription above, which clears
		// any pending timer. A null response does not notify it, so retry once before restoring the
		// previous HTML and stopping the spinner.
		const scheduleSpinnerFallback = () => {
			schedulePostEditTimer(() => setIsRefetching(false), POST_EDIT_RETRY_DELAY_MS);
		};
		const scheduleRetry = () => {
			schedulePostEditTimer(() => {
				scheduleSpinnerFallback();
				ctx.triggerRefetch(localId, 'edit');
			}, POST_EDIT_RETRY_DELAY_MS);
		};

		schedulePostEditTimer(() => {
			scheduleRetry();
			ctx.triggerRefetch(localId, 'edit');
		}, REFETCH_DELAY_MS);

		return cancelPostEditRefetch;
	}, [ctx, localId, cancelPostEditRefetch]);

	const deferPostEditRefetchUntilDraftSync = React.useCallback(() => {
		if (!ctx || !localId) return undefined;
		const removeDeferredRefetch = ctx.deferUntilDraftSync(localId, () => {
			startPostEditRefetch();
		});
		return () => {
			removeDeferredRefetch();
			cancelPostEditRefetch();
		};
	}, [ctx, localId, startPostEditRefetch, cancelPostEditRefetch]);

	useEffect(() => {
		if (!ctx || !localId || !isStatic) {
			return undefined;
		}
		if (!isEditing || !isLivePage) {
			return undefined;
		}
		if (!ctx.hasMountedBefore(localId)) {
			ctx.markMounted(localId);
			return undefined;
		}
		if (nestedMacroLocalIds.length > 0) {
			return startPostEditRefetch();
		}
		return deferPostEditRefetchUntilDraftSync();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const guestParamsRef = React.useRef<string | undefined>(undefined);
	const serializedGuestParams = React.useMemo(
		() => JSON.stringify(guestParams ?? null),
		[guestParams],
	);

	const html = localId ? store?.html.get(localId) : undefined;
	const errorCode = localId ? store?.errors.get(localId) : undefined;
	const resultMetadata = localId ? store?.resultMetadata.get(localId) : undefined;

	useStaticMacroRenderAnalytics({
		html,
		errorCode,
		result: resultMetadata,
		isEditing: Boolean(isEditing),
		isLivePage: Boolean(isLivePage),
	});

	// Signal cc-puppeteer that this static macro has finished rendering.
	// cc-puppeteer's waitForAllExtensionReadyEvents() waits for EXTENSION_READY from every
	// Forge extension it finds in the ADF (regardless of emitsReadyEvent manifest declaration).
	// Without this signal, the full 10s timeout is consumed on every PDF export with a static macro.
	// Guarded by a ref so it only fires once per ForgeMacro instance.
	useEffect(() => {
		if (html && localId && !readyEventEmittedRef.current) {
			readyEventEmittedRef.current = true;
			window.postMessage(
				{ source: 'forge-extension', type: 'EXTENSION_READY', localId },
				window.location.origin,
			);
		}
	}, [html, localId]);

	// When a config changes, we want to trigger a re-render.
	useEffect(() => {
		if (!ctx || !localId || !isStatic || !isEditing) return undefined;
		const serialized = serializedGuestParams;
		const prev = guestParamsRef.current;
		guestParamsRef.current = serialized;
		// Skip on first render for this macro instance — only refetch on subsequent changes.
		if (prev === undefined || prev === serialized) return undefined;
		return deferPostEditRefetchUntilDraftSync();
	}, [
		ctx,
		localId,
		isStatic,
		isEditing,
		serializedGuestParams,
		deferPostEditRefetchUntilDraftSync,
	]);

	const isFullyLoaded = !!store?.fullyLoaded;
	useEffect(() => {
		if (!ctx || !localId || !isStatic || !isEditing) return undefined;
		if (!isFullyLoaded) return undefined;
		if (html !== undefined || errorCode !== undefined) return undefined;

		const currentStore = ctx.storeRef.current;
		if (!currentStore || currentStore.insertionRefetchGaveUp.has(localId)) return undefined;

		let giveUpTimeoutId: ReturnType<typeof setTimeout> | undefined;
		const scheduleIframeFallback = (deadline: number) => {
			giveUpTimeoutId = setTimeout(
				() => {
					currentStore.insertionRefetchGaveUp.add(localId);
					forceUpdate();
				},
				Math.max(0, deadline - Date.now()),
			);
		};

		const startedAt = currentStore.insertionRefetchStartedAt.get(localId);
		if (startedAt !== undefined) {
			scheduleIframeFallback(startedAt + INSERTION_REFETCH_GIVE_UP_DELAY_MS);
			return () => clearTimeout(giveUpTimeoutId);
		}

		let started = false;
		const startInsertionRefetch = () => {
			if (started) return;
			started = true;
			const firedAt = Date.now();
			currentStore.insertionRefetchStartedAt.set(localId, firedAt);
			ctx.triggerRefetch(localId, 'insertion');
			scheduleIframeFallback(firedAt + INSERTION_REFETCH_GIVE_UP_DELAY_MS);
		};

		let fetchTimeoutId = setTimeout(startInsertionRefetch, INSERTION_REFETCH_START_DELAY_MS);
		const removeDeferredRefetch = ctx.deferUntilDraftSync(localId, () => {
			clearTimeout(fetchTimeoutId);
			fetchTimeoutId = setTimeout(startInsertionRefetch, INSERTION_REFETCH_START_DELAY_MS);
		});

		return () => {
			removeDeferredRefetch();
			clearTimeout(fetchTimeoutId);
			clearTimeout(giveUpTimeoutId);
		};
	}, [ctx, localId, isStatic, isEditing, isFullyLoaded, html, errorCode, forceUpdate]);

	if (!ctx || !localId || !store) {
		return renderIframe();
	}

	if (isRefetching) {
		return (
			<div css={spinnerWrapperStyles}>
				<Spinner size="small" />
			</div>
		);
	}

	if (html) {
		return (
			<div
				data-testid="forge-static-macro-content"
				// eslint-disable-next-line react/no-danger
				dangerouslySetInnerHTML={{ __html: html }}
			/>
		);
	}

	if (errorCode) {
		return renderIframe();
	}

	// Once the query is fully loaded, any macro without html/error is not a static macro → iframe.
	// Also fall back immediately if we KNOW this extension doesn't support staticView —
	// only treat !isStatic as definitive when extensions have actually been loaded.
	// If extensions aren't loaded yet, show spinner and wait (preloader may still be in-flight).
	const isAwaitingInsertedContent =
		isStatic && isEditing && !store.insertionRefetchGaveUp.has(localId);

	if ((store.fullyLoaded || (extensionsLoaded && !isStatic)) && !isAwaitingInsertedContent) {
		return renderIframe();
	}

	// Extension supports staticView but data hasn't arrived yet — show spinner briefly.
	// Use the same testid as the iframe loader so cc-puppeteer waits for it to disappear.
	return (
		<div css={spinnerWrapperStyles} data-testid="forge-app-loading-spinner">
			<Spinner size="small" />
		</div>
	);
}
